'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface Category {
  id: number;
  name: string;
  slug: string;
  type: string;
  parentId: number | null;
  listDisplayText?: string;
  backgroundImage?: string;
}

interface CaseItem {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  image: string | null;
  imageUrl: string; // 新闻API返回的图片URL字段
  publishedAt: string; // 新闻API使用publishedAt而不是createdAt
  category: Category;
}

async function fetchCategories(): Promise<Category[]> {
  try {
    const response = await fetch('/api/categories');
    const result = await response.json();
    
    if (result.success && Array.isArray(result.data)) {
      // 过滤案例分类 - 只显示案例中心的下级栏目
      const caseCategories = result.data.filter((cat: any) => 
        cat.type === 'case' && cat.parentId !== null
      );
      return caseCategories;
    }
    return [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

async function fetchCases(page: number = 1, pageSize: number = 9, categorySlug?: string): Promise<{
  cases: CaseItem[];
  total: number;
  totalPages: number;
}> {
  try {
    // 直接调用新闻API，获取栏目ID为33的新闻数据
    const url = `/api/news?page=${page}&limit=${pageSize}&categoryId=33`;
    
    const response = await fetch(url);
    const result = await response.json();
    
    
    
    // 处理数据结构 - 新闻API返回的是 { news: [], pagination: {} }
    let casesList = [];
    let total = 0;
    
    if (result.news && Array.isArray(result.news)) {
      casesList = result.news;
      total = result.pagination?.total || result.news.length;
    } else if (Array.isArray(result)) {
      casesList = result;
      total = result.length;
    }
    
    
    
    return {
      cases: casesList,
      total,
      totalPages: Math.ceil(total / pageSize),
    };
  } catch (error) {
    console.error('Error fetching cases:', error);
    return {
      cases: [],
      total: 0,
      totalPages: 1,
    };
  }
}

function formatDate(date: Date): string {
  return new Date(date).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function extractFirstImage(content: string): string | null {
  const imgRegex = /<img[^>]+src="([^"]+)"/g;
  const match = imgRegex.exec(content);
  return match ? match[1] : null;
}

export default function CasePage() {
  const router = useRouter();
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCases, setTotalCases] = useState(0);
  const [currentCategory, setCurrentCategory] = useState('');
  const [caseCategory, setCaseCategory] = useState<Category | null>(null);
  const pageSize = 9; // 每页显示9个案例（3x3网格布局）

  // 获取分类数据
  useEffect(() => {
    const fetchCategoriesData = async () => {
      try {
        const categoriesData = await fetchCategories();
        setCategories(categoriesData);
        
        // 获取案例中心栏目信息
        const response = await fetch('/api/categories');
        const result = await response.json();

        
        if (result.success && Array.isArray(result.data)) {
          // 获取案例中心栏目（ID为33）
          const caseCenterCategory = result.data.find((cat: any) => cat.id === 33);
  
          if (caseCenterCategory) {

            setCaseCategory(caseCenterCategory);
          } else {

          }
        }
      } catch (err) {
        console.error('获取分类数据失败:', err);
      }
    };

    fetchCategoriesData();
  }, []);

  // 获取案例数据
  useEffect(() => {
    const fetchCasesData = async () => {
      try {
        const { cases: casesData, total, totalPages: pages } = await fetchCases(currentPage, pageSize, currentCategory);
        setCases(casesData);
        setTotalCases(total);
        setTotalPages(pages);
      } catch (err) {
        console.error('获取案例数据失败:', err);
      }
      
      setLoading(false);
    };

    fetchCasesData();
  }, [currentPage, currentCategory]);

  // 处理分页
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading || !caseCategory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <Navbar />
        <div className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-800 rounded w-1/4 mb-4"></div>
              <div className="h-4 bg-gray-800 rounded w-1/3 mb-8"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-gray-800/50 rounded-lg p-6 h-64">
                    <div className="h-4 bg-gray-700 rounded mb-4"></div>
                    <div className="h-4 bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-black relative">
      {/* 导航栏 */}
      <Navbar />
      
      {/* 动态背景装饰 */}
      <div className="absolute inset-0 opacity-10 z-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-orange-500/20 to-red-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* 主要内容区域 */}
      <div className="relative z-20">
        
        {/* 页面头部 */}
        <section className="relative py-32 overflow-hidden">
          {/* 背景图 */}
          <div className="absolute inset-0 z-0">
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: caseCategory?.backgroundImage 
                  ? `url('${caseCategory.backgroundImage}')`
                  : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: 'brightness(0.8) contrast(1.2)'
              }}
            >
              {/* 渐变遮罩层 */}
              <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
            </div>
          </div>
          
          <div className="absolute inset-0 opacity-20 z-10">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/30 to-purple-600/30 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-gradient-to-r from-orange-500/30 to-red-600/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-gradient-to-r from-cyan-500/30 to-blue-600/30 rounded-full blur-3xl animate-pulse delay-2000"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 text-center z-20">
            <h1 
              className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-6"
            >
              {caseCategory?.name}
            </h1>
            <p 
              className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-12"
            >
              {caseCategory?.listDisplayText}
            </p>
            
            {/* 装饰性元素 */}
            <div className="flex justify-center items-center space-x-4 text-blue-300/80">
              <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
              <span className="text-sm font-medium drop-shadow-md">●</span>
              <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-purple-400 to-transparent"></div>
            </div>
          </div>
          
          {/* 底部渐变过渡 */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent z-20"></div>
        </section>

        {/* 案例列表 */}
        <section className="py-20 relative z-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* 分类筛选 */}
            <div className="mb-12">
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  href="/case"
                  className="px-6 py-3 rounded-full font-semibold bg-orange-500 text-white transition-all duration-300"
                >
                  全部
                </Link>
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    href={`/case/category/${category.slug}`}
                    className="px-6 py-3 rounded-full font-semibold bg-gray-800 text-gray-300 hover:bg-gray-700 transition-all duration-300"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* 案例列表 */}
            {cases.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-8">
                {cases.map((item, index) => {
                  // 使用新闻API返回的imageUrl字段，如果不存在则使用image字段
                  const imageUrl = item.imageUrl || item.image || '/uploads/case/default.jpg';
                  
                  return (
                    <div
                      key={item.id}
                      className="group"
                    >
                      <Link href={`/case/${item.id}`}>
                        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-orange-500/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/20">
                          {/* 图片区域 */}
                          <div className="relative h-48 overflow-hidden">
                            <img 
                              src={imageUrl} 
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                            
                            {/* 图标和分类 */}
                            <div className="absolute top-4 left-4 flex items-center space-x-2">
                              <div className="text-2xl">📋</div>
                              <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full text-sm font-medium">
                                {item.category?.name || '案例'}
                              </span>
                            </div>
                          </div>
                          
                          {/* 内容区域 */}
                          <div className="p-6">
                            <h3 className="text-xl font-bold text-white mb-3 group-hover:text-orange-400 transition-colors duration-300 truncate">
                              {item.title}
                            </h3>
                            <p className="text-gray-300 text-sm leading-relaxed mb-4 line-clamp-2">
                              {item.summary}
                            </p>
                            
                            {/* 查看详情按钮 */}
                            <div className="flex items-center justify-between">
                              <span className="text-orange-400 text-sm font-medium">查看详情</span>
                              <svg className="w-5 h-5 text-orange-400 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">📋</div>
                <h3 className="text-2xl font-bold text-white mb-2">暂无案例</h3>
                <p className="text-gray-400">当前分类下还没有案例内容</p>
              </div>
            )}

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-12">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  上一页
                </button>
                
                <div className="flex space-x-2">
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        currentPage === index + 1
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  下一页
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
      
      {/* 页脚 */}
      <Footer />
    </main>
  );
}